/**
 * Centralized Schema.org JSON-LD structured data definitions
 * All schemas are managed here for easy maintenance and SEO optimization
 */

import {
  defaultLocale,
  getLocalizedPathname,
  localeConfigs,
  locales,
  type Locale,
} from "./i18n";
import { SINGAPORE_OFFICE } from "@/content/singapore/types";

const SITE_URL = "https://www.anchorsprint.com";
const COMPANY_NAME = "Anchor Sprint Sdn Bhd";
const COMPANY_LEGAL_NAME = "Anchor Sprint Sdn Bhd";
const COMPANY_LOGO = `${SITE_URL}/images/logo.png`;
const COMPANY_PHONE = "+60-11-5924-6128";
const COMPANY_EMAIL = "general@anchorsprint.com";

// SINGLE SOURCE OF TRUTH for how we describe ourselves.
//
// AI Overviews quote a page's own self-description close to verbatim — verified
// 2026-08-28: a competitor's Penang page description was restated almost word for
// word in the Overview that cited it. So what the machines read here is what they
// say about us.
//
// Before this, three surfaces disagreed: Organization schema said "leading provider
// of enterprise solutions and digital transformation services", the root llms.txt
// said "software development and cloud architecture ... for SMEs", and the homepage
// meta said "builds custom AI agents on Claude". The AI Overview accordingly
// described us narrowly as "a Malaysian AI deployment partner". Inconsistent input,
// narrow output.
//
// Keep this sentence near-verbatim wherever we describe the company: Organization
// schema, root llms.txt, homepage meta, the partner page and About. Claims are held
// to the standing rules — "member of" never "official/exclusive", HRD Corp approved
// (never SSG), and no "largest/first/only".
//
// Deliberately NOT a headcount. Jazz 2026-08-28: "no need so precise". A number
// dates the moment someone certifies or leaves, and this string is copied across
// several surfaces, so every change would be a multi-file correction. "Architects
// and Associates in house" also carries more than a count does — it says we hold
// BOTH tracks, the technical and the non-technical.
const COMPANY_DESCRIPTION =
  "Anchor Sprint is a Claude-focused agentic AI development agency in Malaysia and Singapore, and Certified in the Claude Partner Network. We cover the full lifecycle — enablement, advisory, build, run and HRD Corp claimable training — with Claude Certified Architects and Associates in house.";

// =============================================================================
// ORGANIZATION SCHEMA - Global (used on all pages via layout)
// =============================================================================
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  "name": COMPANY_NAME,
  "legalName": COMPANY_LEGAL_NAME,
  "url": SITE_URL,
  "logo": COMPANY_LOGO,
  "description": COMPANY_DESCRIPTION,
  "foundingDate": "2020",
  "identifier": {
    "@type": "PropertyValue",
    "propertyID": "SSM",
    "value": "1535934-D"
  },
  "email": COMPANY_EMAIL,
  "telephone": COMPANY_PHONE,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "No. 68-1, Jalan Setia Utama AS U13/AS, Setia Alam, Seksyen U13",
    "addressLocality": "Shah Alam",
    "addressRegion": "Selangor",
    "postalCode": "40170",
    "addressCountry": "MY"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": COMPANY_PHONE,
    "email": COMPANY_EMAIL,
    "contactType": "customer service",
    "availableLanguage": ["English", "Malay", "Chinese"]
  },
  "areaServed": [
    {
      "@type": "State",
      "name": "Selangor"
    },
    {
      "@type": "City",
      "name": "Kuala Lumpur"
    },
    {
      "@type": "City",
      "name": "Petaling Jaya"
    },
    {
      "@type": "City",
      "name": "Shah Alam"
    }
  ],
  // Our own product brand (2026-10-31). EzyChat lives at ezychat.ai; the
  // "EZYCHAT" word mark is being filed under Anchor Sprint Sdn Bhd. Only a Brand
  // here: a nested Product or SoftwareApplication without ratings would show as
  // an invalid rich-result item in Search Console.
  "brand": {
    "@type": "Brand",
    "name": "EzyChat",
    "url": "https://ezychat.ai/",
    "logo": "https://www.anchorsprint.com/images/ezychat/ezychat-logo.webp"
  },
  "knowsAbout": [
    "Software Development",
    "Artificial Intelligence",
    "Machine Learning",
    "Cloud Computing",
    "AWS Cloud Services",
    "Digital Transformation",
    "System Integration",
    "Enterprise Solutions",
    "Enterprise IT Procurement",
    "Software Licensing",
    "IT Hardware Supply",
    "Mobile App Development",
    "Web Development",
    "Claude",
    "ChatGPT",
    "OpenAI API",
    "AI agents"
  ],
  // Programme memberships (2026-10-08). schema.org memberOf accepts Organization
  // or ProgramMembership; ProgramMembership names the programme and its host
  // without implying the host is our parent org. Wording is held to each
  // programme's approved line: "Certified in the Claude Partner Network" (no
  // tier/track wording, none is published on the site) and "OpenAI Select
  // Partner". Never "official", "endorsed" or "certified by".
  "memberOf": [
    {
      "@type": "ProgramMembership",
      "name": "Certified in the Claude Partner Network",
      "programName": "Claude Partner Network",
      "hostingOrganization": {
        "@type": "Organization",
        "name": "Anthropic",
        "url": "https://www.anthropic.com"
      }
    },
    {
      "@type": "ProgramMembership",
      "name": "OpenAI Select Partner",
      "programName": "OpenAI Partner Network",
      "hostingOrganization": {
        "@type": "Organization",
        "name": "OpenAI",
        "url": "https://openai.com"
      }
    }
  ],
  // schema.org `award` is Text; the previous {"@type": "Award"} object used a
  // type that does not exist in the vocabulary.
  "award": "Malaysia Digital status, certified by MDEC (Malaysia Digital Economy Corporation), 2024",
  "sameAs": [
    "https://www.linkedin.com/company/anchor-sprint-sdn-bhd/",
    "https://www.facebook.com/people/Anchor-Sprint/61569209893231/",
    "https://www.instagram.com/anchorsprint/"
  ]
};

// =============================================================================
// WEBSITE SCHEMA - Global (used on all pages via layout)
// =============================================================================
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "name": COMPANY_NAME,
  "url": SITE_URL,
  // Same codes as hreflang and <html lang> (en-MY / ms-MY / zh-MY) — taken from
  // localeConfigs so the three surfaces cannot drift apart again (this said
  // "zh-CN" while every page declared zh-MY).
  "inLanguage": locales.map((locale) => localeConfigs[locale].hreflang),
  "publisher": {
    "@type": "Organization",
    "name": COMPANY_NAME,
    "logo": {
      "@type": "ImageObject",
      "url": COMPANY_LOGO
    }
  }
};

// =============================================================================
// SINGAPORE LOCAL BUSINESS SCHEMA
// =============================================================================
// The Singapore pages previously inherited the Malaysian localBusinessSchema, so
// every /singapore/* route declared addressCountry MY, addressLocality Shah Alam
// and areaServed Selangor. Visible copy said Suntec and Pte. Ltd.; the structured
// data said Selangor. That contradiction is why Google served the Malaysian
// partner page for "claude partner singapore" instead of the Singapore one.
//
// NAP is taken from SINGAPORE_OFFICE (content/singapore/types.ts) so the schema
// and the visible address block cannot drift apart.
export const singaporeLocalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/singapore/#localbusiness`,
  "name": SINGAPORE_OFFICE.legalName,
  "legalName": SINGAPORE_OFFICE.legalName,
  "alternateName": SINGAPORE_OFFICE.name,
  "identifier": {
    "@type": "PropertyValue",
    "propertyID": "UEN",
    "name": "UEN",
    "value": SINGAPORE_OFFICE.uen
  },
  "foundingDate": "2026-07-19",
  "image": COMPANY_LOGO,
  "logo": { "@type": "ImageObject", "url": COMPANY_LOGO },
  "description":
    "Claude partner and AI agent development in Singapore, Certified in the Claude Partner Network, delivering from Suntec Tower One with PDPC-aligned governance.",
  "url": `${SITE_URL}/singapore/`,
  // Pre-existing temporary +65 line, shown in the visible NAP block on every
  // /singapore/ page — the schema mirrors what the page says.
  "telephone": SINGAPORE_OFFICE.phone,
  "faxNumber": SINGAPORE_OFFICE.fax,
  "email": COMPANY_EMAIL,
  "priceRange": "$$",
  "currenciesAccepted": "SGD",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": SINGAPORE_OFFICE.street,
    "addressLocality": SINGAPORE_OFFICE.city,
    "postalCode": SINGAPORE_OFFICE.postalCode,
    "addressCountry": SINGAPORE_OFFICE.country
  },
  "areaServed": [{ "@type": "Country", "name": "Singapore" }],
  // Points at the sitewide Organization node (layout.tsx) rather than
  // restating it, so the SG entity and the group entity resolve as one graph.
  "parentOrganization": { "@id": `${SITE_URL}/#organization` }
};

// =============================================================================
// LOCAL BUSINESS SCHEMA - For local SEO in Selangor & Kuala Lumpur
// =============================================================================
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#localbusiness`,
  "name": COMPANY_NAME,
  "image": COMPANY_LOGO,
  "logo": {
    "@type": "ImageObject",
    "url": COMPANY_LOGO
  },
  "description": "Leading software development and IT consulting company in Shah Alam, Selangor, serving businesses across Kuala Lumpur and Malaysia.",
  "url": SITE_URL,
  "telephone": COMPANY_PHONE,
  "email": COMPANY_EMAIL,
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "No. 68-1, Jalan Setia Utama AS U13/AS, Setia Alam, Seksyen U13",
    "addressLocality": "Shah Alam",
    "addressRegion": "Selangor",
    "postalCode": "40170",
    "addressCountry": "MY"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 3.107895,
    "longitude": 101.458952
  },
  "hasMap": "https://maps.google.com/?q=3.107895,101.458952",
  "areaServed": [
    {
      "@type": "State",
      "name": "Selangor"
    },
    {
      "@type": "City",
      "name": "Shah Alam"
    },
    {
      "@type": "City",
      "name": "Kuala Lumpur"
    },
    {
      "@type": "City",
      "name": "Petaling Jaya"
    },
    {
      "@type": "City",
      "name": "Subang Jaya"
    }
  ],
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "paymentAccepted": "Cash, Credit Card, Bank Transfer",
  "currenciesAccepted": "MYR",
  "sameAs": [
    "https://www.linkedin.com/company/anchor-sprint-sdn-bhd/",
    "https://www.facebook.com/people/Anchor-Sprint/61569209893231/",
    "https://www.instagram.com/anchorsprint/"
  ]
};

// =============================================================================
// SERVICE SCHEMA - Per service page
// =============================================================================
export const getServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  // Overrides the default Selangor/KL service area. Needed for the Singapore
  // pages, which otherwise declare themselves as serving Malaysia — the exact
  // signal that made Google prefer the Malaysian partner page for SG queries.
  areaServed?: Array<Record<string, unknown>>;
  offers?: Array<{
    name: string;
    description: string;
    availability?: string;
    price?: string;
    priceCurrency?: string;
    url?: string;
  }>;
}) => {
  const toAbsoluteSiteUrl = (url: string) =>
    url.startsWith("http") ? url : `${SITE_URL}${url}`;
  const serviceUrl = toAbsoluteSiteUrl(service.url);

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "url": serviceUrl,
    ...(service.serviceType ? { "serviceType": service.serviceType } : {}),
    "provider": {
      "@type": "Organization",
      "name": COMPANY_NAME,
      "url": SITE_URL
    },
    ...(service.offers && service.offers.length > 0
      ? {
          offers: service.offers.map((offer) => ({
            "@type": "Offer",
            name: offer.name,
            description: offer.description,
            url: offer.url ? toAbsoluteSiteUrl(offer.url) : serviceUrl,
            availability: offer.availability || "https://schema.org/InStock",
            ...(offer.price ? { price: offer.price } : {}),
            ...(offer.priceCurrency ? { priceCurrency: offer.priceCurrency } : {})
          }))
        }
      : {}),
    ...(service.areaServed
      ? { "areaServed": service.areaServed }
      : {
    // Countries lead, cities follow. Google's AI Overview reads the FIRST
    // areaServed entries when it characterises who we are — with Selangor at the
    // head it described us as operating "out of Selangor", which is narrower
    // than the business actually is (Jazz, 2026-08-07). The Shah Alam / KL / PJ
    // entries stay for local-pack relevance; the LocalBusiness schema in the
    // root layout still carries the full Shah Alam NAP either way.
    "areaServed": [
      {
        "@type": "Country",
        "name": "Malaysia"
      },
      {
        "@type": "Country",
        "name": "Singapore"
      },
      {
        "@type": "State",
        "name": "Selangor",
        "containedIn": {
          "@type": "Country",
          "name": "Malaysia"
        }
      },
      {
        "@type": "City",
        "name": "Shah Alam",
        "containedIn": {
          "@type": "State",
          "name": "Selangor",
          "containedIn": {
            "@type": "Country",
            "name": "Malaysia"
          }
        }
      },
      {
        "@type": "City",
        "name": "Kuala Lumpur",
        "containedIn": {
          "@type": "Country",
          "name": "Malaysia"
        }
      },
      {
        "@type": "City",
        "name": "Petaling Jaya",
        "containedIn": {
          "@type": "State",
          "name": "Selangor",
          "containedIn": {
            "@type": "Country",
            "name": "Malaysia"
          }
        }
      },
      {
        "@type": "City",
        "name": "Subang Jaya",
        "containedIn": {
          "@type": "State",
          "name": "Selangor",
          "containedIn": {
            "@type": "Country",
            "name": "Malaysia"
          }
        }
      }
    ]
    }),
  };
};

// =============================================================================
// ARTICLE/BLOG SCHEMA - Per blog post
// =============================================================================
// Google requires ISO 8601 dates on Article schema; blog data stores
// human-readable display dates, so normalize here.
const toIsoDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 10);
};

export const getArticleSchema = (article: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  tags?: string[];
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "url": `${SITE_URL}${article.url}`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}${article.url}`
    },
    "datePublished": toIsoDate(article.datePublished),
    "dateModified": toIsoDate(article.dateModified || article.datePublished),
    "author": {
      "@type": "Organization",
      "name": COMPANY_NAME,
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": COMPANY_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": COMPANY_LOGO
      }
    },
    ...(article.image && { "image": article.image.startsWith("http") ? article.image : `${SITE_URL}${article.image}` }),
    ...(article.tags && { "keywords": article.tags.join(", ") })
  };
};

// =============================================================================
// FAQ SCHEMA - For pages with FAQ sections
// =============================================================================
export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};



// =============================================================================
// BREADCRUMB SCHEMA - For navigation structure
// =============================================================================
export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.url}`
    }))
  };
};

// =============================================================================
// WEBPAGE SCHEMA - For general pages
// =============================================================================
export const getWebPageSchema = (page: {
  name: string;
  url: string;
  description: string;
  type?: string;
  /** ISO date. Worth setting on pages whose value depends on being current —
   *  pricing above all. Answer engines use it to decide whether a page is still
   *  safe to quote, and a pricing page with no dateModified reads as undated. */
  dateModified?: string;
  /** Override for pages whose content is not English (e.g. a Malay template preview
   *  served on an English-only route). Defaults to en-SG / en-MY below. */
  inLanguage?: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": page.type || "WebPage",
    "name": page.name,
    "url": `${SITE_URL}${page.url}`,
    "description": page.description,
    ...(page.dateModified ? { "dateModified": page.dateModified } : {}),
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "about": { "@id": `${SITE_URL}/#organization` },
    // /singapore/ pages are en-SG (matching their hreflang and <html lang>);
    // everything else defaults to en-MY. /ms/ and /zh/ variants are rewritten
    // by localizePageSchemas() when the catch-all route renders them.
    "inLanguage": page.inLanguage || (page.url.startsWith("/singapore/") ? "en-SG" : "en-MY")
  };
};

/**
 * Re-points per-page schemas at a translated route.
 *
 * schema-configs.ts holds one English entry per route, and the /ms/ and /zh/
 * catch-all reuses it. Without this, a /ms/ page shipped a WebPage whose `url`
 * was the English page and whose `inLanguage` was "en-MY". Only top-level
 * schemas carrying a string `inLanguage` (WebPage and its subtypes) are
 * touched — those are the "this document" schemas. Service/Breadcrumb/FAQ
 * entities and the Organization/WebSite globals are left as they are.
 *
 * `meta` is the route's `localized[locale]` block from meta-configs.ts. When
 * present, its title/description replace the English WebPage name/description,
 * so a /zh/ page no longer describes itself in English. A missing block (or
 * field) keeps the English value.
 */
export const localizePageSchemas = (
  schemas: object[],
  locale: Locale,
  meta?: { title?: string; description?: string },
): object[] => {
  if (locale === defaultLocale) return schemas;

  const localizedName = meta?.title ? stripBrandSuffix(meta.title) : undefined;
  const localizedDescription = meta?.description;

  const prefix = SITE_URL;
  return schemas.map((schema) => {
    const record = schema as Record<string, unknown>;
    if (typeof record.inLanguage !== "string") return schema;

    const localized: Record<string, unknown> = {
      ...record,
      inLanguage: localeConfigs[locale].hreflang,
    };
    if (typeof record.url === "string" && record.url.startsWith(prefix)) {
      const path = record.url.slice(prefix.length) || "/";
      const localizedPath = getLocalizedPathname(path, locale);
      // English-only routes have no /ms/ or /zh/ twin; leave them untouched.
      if (localizedPath === getLocalizedPathname(path, defaultLocale)) {
        return schema;
      }
      localized.url = `${prefix}${localizedPath}`;
    }
    if (localizedName) localized.name = localizedName;
    if (localizedDescription) localized.description = localizedDescription;
    return localized;
  });
};

/** Meta titles carry a trailing "| Anchor Sprint" brand suffix that WebPage names do not. */
const stripBrandSuffix = (title: string): string =>
  title.replace(/\s*[|｜]\s*(Anchor Sprint|安克极速)\s*$/u, "").trim() || title;

// =============================================================================
// JOB POSTING SCHEMA - For careers / open roles (Google Jobs rich results)
// =============================================================================
export const getJobPostingSchema = (job: {
  title: string;
  description: string;
  employmentType: string; // "FULL_TIME" | "INTERN" | "PART_TIME" | "CONTRACTOR"
  datePosted: string;     // ISO date, e.g. "2026-06-15"
  validThrough: string;   // ISO date/datetime
  url: string;            // role page, e.g. "/careers/forward-deployed-engineer/"
  identifier?: string;    // stable role id, e.g. the slug
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.datePosted,
    "validThrough": job.validThrough,
    "employmentType": job.employmentType,
    "url": `${SITE_URL}${job.url}`,
    ...(job.identifier && {
      "identifier": {
        "@type": "PropertyValue",
        "name": COMPANY_NAME,
        "value": job.identifier
      }
    }),
    "directApply": false,
    "hiringOrganization": {
      "@type": "Organization",
      "name": COMPANY_NAME,
      "sameAs": SITE_URL,
      "logo": COMPANY_LOGO
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Shah Alam",
        "addressRegion": "Selangor",
        "addressCountry": "MY"
      }
    },
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "Malaysia"
    }
  };
};

// =============================================================================
// COLLECTION PAGE SCHEMA - For listing/directory pages
// =============================================================================
export const getCollectionPageSchema = (page: {
  name: string;
  url: string;
  description: string;
}) => {
  return getWebPageSchema({ ...page, type: "CollectionPage" });
};

// =============================================================================
// ITEM LIST SCHEMA - For pages listing items
// =============================================================================
export const getItemListSchema = (list: {
  name: string;
  description?: string;
  items: Array<{ name: string; url?: string; description?: string }>;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": list.name,
    ...(list.description && { "description": list.description }),
    "numberOfItems": list.items.length,
    "itemListElement": list.items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url && { "url": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}` }),
      ...(item.description && { "description": item.description })
    }))
  };
};

// =============================================================================
// SOFTWARE APPLICATION SCHEMA - For product pages
// =============================================================================
export const getSoftwareApplicationSchema = (app: {
  name: string;
  url: string;
  description: string;
  applicationCategory?: string;
  operatingSystem?: string;
  featureList?: string[];
  offers?: {
    availability?: string;
    description?: string;
    price?: string;
    priceCurrency?: string;
  };
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": app.name,
    "url": `${SITE_URL}${app.url}`,
    "description": app.description,
    "applicationCategory": app.applicationCategory || "BusinessApplication",
    ...(app.operatingSystem && { "operatingSystem": app.operatingSystem }),
    ...(app.featureList && { "featureList": app.featureList }),
    ...(app.offers && {
      "offers": {
        "@type": "Offer",
        ...app.offers
      }
    }),
    "provider": {
      "@type": "Organization",
      "name": COMPANY_NAME,
      "url": SITE_URL
    }
  };
};

// =============================================================================
// HOWTO SCHEMA - For process/step pages
// =============================================================================
export const getHowToSchema = (howTo: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string }>;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howTo.name,
    "description": howTo.description,
    "step": howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  };
};

// =============================================================================
// SERVICE PAGE SCHEMAS - Pre-defined for each service
// =============================================================================
export const serviceSchemas: Record<string, ReturnType<typeof getServiceSchema>> = {
  "as": getServiceSchema({
    name: "AI Agent Development, Claude Managed Service & Training Malaysia",
    description: "AI agent development, Claude onboarding and managed operations, and practical Claude and AI training for Malaysian businesses. Delivered by a local Malaysia Digital certified team with more than 15 years of technology experience.",
    url: "/services/ai-solutions/"
  }),
  "sds": getServiceSchema({
    name: "Software Development Services Selangor & Kuala Lumpur",
    description: "Custom software development, mobile apps, and enterprise solutions for businesses in Shah Alam, Selangor, Kuala Lumpur, and across Malaysia.",
    url: "/services/software-development/"
  }),
  "sis": getServiceSchema({
    name: "System Integration Services Selangor & Kuala Lumpur",
    description: "API development, CRM/ERP integration, and middleware solutions for businesses in Shah Alam, Selangor, and Kuala Lumpur.",
    url: "/services/system-integration/"
  }),
  "dtc": getServiceSchema({
    name: "Digital Transformation Consulting Selangor & Kuala Lumpur",
    description: "Strategic digital transformation and technology roadmap planning for enterprises in Shah Alam, Selangor, Kuala Lumpur, and Petaling Jaya.",
    url: "/services/digital-transformation/"
  }),
  "te": getServiceSchema({
    name: "HRD Corp Certified Training Partner Malaysia",
    description: "HRD Corp certified training programmes covering AI, digital transformation, and modern finance for teams in Shah Alam, Selangor, Kuala Lumpur, and across Malaysia.",
    url: "/services/training-education/"
  }),
  "cadi": getServiceSchema({
    name: "Cloud Architecture Services Selangor & Kuala Lumpur",
    description: "AWS cloud migration, multi-cloud strategies, and cloud-native design for businesses in Shah Alam, Selangor, and Kuala Lumpur.",
    url: "/services/cloud-architecture/"
  }),
  "wdh": getServiceSchema({
    name: "Affordable Web Design Malaysia from RM 1,800",
    description: "Custom web design and hosting in Malaysia from RM 1,800. One-off payment, you own everything, no lock-in. Modern Next.js websites for SMEs in KL, Selangor, and across Malaysia — delivered in as fast as 7 working days.",
    url: "/services/website-development/",
    serviceType: "Website Development",
    offers: [
      {
        name: "Starter Website",
        description: "Professional 1-page website for startups and product launches — delivered in 7 working days",
        price: "1800",
        priceCurrency: "MYR"
      },
      {
        name: "Business Website",
        description: "Up to 5-page professional website for established businesses — delivered in 14 working days",
        price: "3000",
        priceCurrency: "MYR"
      },
      {
        name: "Professional Website",
        description: "Up to 10-page premium website with video and extended support — delivered in 21 working days",
        price: "5000",
        priceCurrency: "MYR"
      },
      {
        name: "Custom Website",
        description: "Fully bespoke website with unlimited pages, multi-language, and CMS dashboard — scope-based timeline and pricing"
      }
    ]
  }),
  "hsp": getServiceSchema({
    name: "Hardware & Software Provisioning Selangor",
    description: "Enterprise hardware procurement, software licensing, and infrastructure setup for businesses in Shah Alam, Selangor, and Kuala Lumpur.",
    url: "/services/hardware-software/"
  }),
  "pm": getServiceSchema({
    name: "Product Management Services Selangor & Kuala Lumpur",
    description: "Product strategy, roadmap planning, and product lifecycle management for businesses in Shah Alam, Selangor, and Kuala Lumpur.",
    url: "/services/product-management/"
  }),
  "sm": getServiceSchema({
    name: "Smart Manufacturing Solutions Selangor",
    description: "Industry 4.0 solutions, IoT implementations, and manufacturing automation for factories in Selangor, Penang, and Johor.",
    url: "/services/smart-manufacturing/"
  })
};
